import { Component, computed, HostListener, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { GridLayoutComponent, GridLayoutItemComponent } from '@progress/kendo-angular-layout';
import { FormFieldComponent, NumericTextBoxComponent } from '@progress/kendo-angular-inputs';
import { LanguageService } from 'src/app/services/language.service';
import { LabelComponent } from '@progress/kendo-angular-label';
import { Accessory, Bike } from 'src/app/models/product.model';
import { TranslatePipe } from '@ngx-translate/core';
import { NgClass, NgStyle } from '@angular/common';
import { logger } from 'src/app/utils';
import {
  DiscountCardSelectionComponent
} from 'src/app/pages/form/bikes-selection/discount-card-selection/discount-card-selection.component';
import { TestDataService } from 'src/app/services/testData.service';
import { Subscription } from 'rxjs';
import { FormService } from 'src/app/services/form.service';
import { SpacerComponent } from 'src/app/ui/atoms/spacer/spacer.component';
import { EXCLUDED_BIKE_TYPES_HEIGHT_INSERTION, EXTRA_ACCESSORIES_IN_BIKE_SELECTION_PAGE } from 'src/app/app.constants';

@Component({
    selector: 'app-bikes-selection',
    imports: [
        LabelComponent,
        FormsModule,
        NumericTextBoxComponent,
        NgClass,
        GridLayoutComponent,
        GridLayoutItemComponent,
        FormFieldComponent,
        ReactiveFormsModule,
        TranslatePipe,
        DiscountCardSelectionComponent,
        SpacerComponent,
        NgStyle,
    ],
    templateUrl: './bikes-selection.component.html',
    styleUrl: './bikes-selection.component.scss'
})
export class BikesSelectionComponent implements OnInit, OnDestroy {
  private testDataService = inject(TestDataService);
  private testDataSubscription: Subscription;

  private languageService = inject(LanguageService);
  private formService = inject(FormService);

  lang = computed(() => this.languageService.currentLanguage().toLowerCase());
  form = this.formService.form;

  readonly Array = Array;
  readonly excludedHeightInsertion = EXCLUDED_BIKE_TYPES_HEIGHT_INSERTION;
  readonly extraAccessoriesInBikeSelectionPage = EXTRA_ACCESSORIES_IN_BIKE_SELECTION_PAGE;

  @Input({required: true}) formId: string;

  formGroup = new FormGroup({} as any);

  bikes = computed(() => this.formService.bikes());

  extraAccInBikePage = computed(() => {
    return this.formService.accessories().filter(a => this.extraAccessoriesInBikeSelectionPage.includes(a.bikeType));
  });

  smallWidth = false;

  private initBikesForm() {
    Object.keys(this.formGroup.controls).forEach(key => {
      if (key.startsWith('bike-')) this.formGroup.removeControl(key);
    });

    this.bikes().forEach(b => {
      const bikeGroup = new FormGroup<any>({quantity: new FormControl()});
      const formName = `bike-${b.bikeType}`;
      if (!this.formGroup.contains(formName)) {
        this.formGroup.addControl(formName, bikeGroup);
      }
    });
  }

  private initAccForm() {
    Object.keys(this.formGroup.controls).forEach(key => {
      if (key.startsWith('accessory-')) this.formGroup.removeControl(key);
    });

    const chosenAccessoriesInBikePage = this.formService.form().accessories
      ?.filter(ea => this.extraAccessoriesInBikeSelectionPage.includes(ea.bikeType)) || [];

    this.extraAccInBikePage()?.forEach(accessory => {
        const quantity = chosenAccessoriesInBikePage?.find(c =>
          c.bikeType === this.formService.accessories().find(e => e.bikeType === accessory.bikeType)?.bikeType
        )?.quantity;
        const accessoryGroup = new FormGroup<any>({quantity: new FormControl(quantity)});
        this.formGroup.addControl(`accessory-${accessory.bikeType}`, accessoryGroup);
      });
  }

  constructor() {
    const sameRental = this.form().previousSameRental || false;
    const hasChosenBikes = (this.form().bikes && this.form().bikes!.length > 0) || false;
    const hasChosenAccessories = (this.form().accessories && this.form().accessories!.length > 0) || false;

    if (sameRental) {
      this.initBikesForm();
      this.initAccForm();

      if (hasChosenBikes || hasChosenAccessories) {
        const savedBikes = this.form().bikes!;

        this.formService.update({
          ...this.formService.form(),
          previousBikes: savedBikes,
        });

        const savedBikeTypes = Array.from(new Set(savedBikes.map(b => b.bikeType)));

        for (const bikeType of savedBikeTypes) {
          const bikeGroup = this.formGroup.get(`bike-${bikeType}`) as FormGroup;
          if (!bikeGroup) {
            logger.w(`Bike group bike-${bikeType} not found during restoration.`);
            continue;
          }
          const bikesOfType = savedBikes.filter(b => b.bikeType === bikeType);
          const correctQuantity = bikesOfType.length;
          const savedHeights = bikesOfType
            .sort((a, b) => a.id - b.id)
            .map(b => b.height);

          bikeGroup.get('quantity')?.setValue(correctQuantity, {emitEvent: false});

          for (let i = 1; i <= correctQuantity; i++) {
            const controlName = `height-${i}`;
            const heightValue = savedHeights[i - 1];
            if (!bikeGroup.contains(controlName)) {
              bikeGroup.addControl(controlName, new FormControl(heightValue, this.heightValidators));
            } else {
              bikeGroup.get(controlName)?.setValue(heightValue, {emitEvent: false});
            }
          }

          const bikeObject = this.bikes().find(b => b.bikeType === bikeType);
          if (bikeObject) {
            bikeObject.quantity = correctQuantity;
          }
        }
      }
    } else if (this.formService.accessoriesViewed()) {
      this.initBikesForm();
      this.initAccForm();
    } else {
      this.fetchProducts();
    }
  }

  ngOnInit() {
    this.onResize();
    this.formGroup.valueChanges.subscribe(() => {
      this.buildDataBikes();
      this.buildDataAccessories();
    });
    this.initTestDataObserver();
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.smallWidth = window.innerWidth < 992
  }

  private fetchProducts() {
    logger.i('fetching bikes and accessories');
    this.formService.fetchBikesForRentalPoint().subscribe((res: any) => {
      this.formService.bikes.update(() => {
        const bikes = res.products.bikes?.filter((b: any) => b?.availability > 0);
        bikes.forEach((b: any) => {
          b.supportedAccessories = b.supportedAccessories?.filter((a: any) => a.availability > 0);
        });
        return bikes;
      });
      this.formService.accessories.update(() => res.products.accessories?.filter((a: any) => a?.availability > 0));

      this.initBikesForm();
      this.initAccForm();

      logger.t('bikes', this.bikes());
    });
  }

  private initTestDataObserver() {
    this.testDataSubscription = this.testDataService.fillDataObserver.subscribe(() => {
      if (document.getElementById(this.formId)) {
        const bikeForms = Object.keys(this.formGroup.controls).filter(key => key.startsWith('bike-'));
        for (const bikeForm of bikeForms) {
          const bikeGroup = this.formGroup.get(bikeForm) as FormGroup;
          const bike = this.bikes().find(b => `bike-${b.bikeType}` === bikeForm);
          if (bike) {
            bikeGroup.get('quantity')?.setValue(0);
            this.onBikeQuantityChange(bike);
            bikeGroup.get('quantity')?.setValue(undefined);
          }
        }

        const randomBikeForms = bikeForms
          .sort(() => 0.5 - Math.random())
          .slice(0, Math.floor(Math.random() * bikeForms.length) + 1);

        for (const bikeForm of randomBikeForms) {
          const bikeGroup = this.formGroup.get(bikeForm) as FormGroup;
          const bike = this.bikes().find(b => `bike-${b.bikeType}` === bikeForm);
          if (bike) {
            bikeGroup.get('quantity')?.setValue(1);
            this.onBikeQuantityChange(bike);
            const heightForms = Object.keys(bikeGroup.controls).filter(key => key.startsWith('height-'));
            const randomHeight = Math.floor(Math.random() * 274) + 1;
            heightForms.forEach((heightForm) => bikeGroup.get(heightForm)?.setValue(randomHeight));
          }
        }

        this.formGroup.markAllAsTouched();
      }
    });
  }

  onBikeQuantityChange(bike: Bike) {
    const bikeGroup = this.formGroup.get(`bike-${bike.bikeType}`) as FormGroup;
    const quantity = bikeGroup.get('quantity')?.value || 0;
    const heights: number[] = [];
    Object.keys(bikeGroup.controls).forEach(controlName => {
      if (controlName.startsWith('height')) {
        const h = bikeGroup.get(controlName)!.value;
        heights.push(h);
      }
    });

    for (let i = heights.length; i < quantity; i++) {
      bikeGroup.addControl(`height-${i + 1}`, new FormControl(heights[i] || null, this.heightValidators));
    }

    bike.quantity = quantity;

    setTimeout(() => {
      const numericTextboxElement = document.getElementById(`bike-quantity-${bike.bikeType}`);
      const inputElement = numericTextboxElement?.querySelector('input.k-input-inner') as HTMLInputElement;
      if (inputElement && document.activeElement !== inputElement) inputElement.focus();
    }, 0);
  }

  private get heightValidators() {
    return [Validators.required, Validators.min(1)]
  }

  submit() {
    this.formGroup.markAllAsTouched();
    this.formGroup.updateValueAndValidity();
    this.buildDataBikes();
  }

  private buildDataAccessories() {
    const aData = this.formGroup.getRawValue();
    const aArray: Accessory[] = [];

    Object.keys(aData)
      .filter(key => key.startsWith('accessory-') && aData[key].quantity > 0)
      .forEach(key => {
        const aType = parseInt(key.split('-')[1]);
        const accessory = aData[key];
        aArray.push({
          ...Object.create(this.extraAccInBikePage()).find((a: Accessory) => a.bikeType === aType)!,
          quantity: accessory.quantity
        });
      });

    const accNotInBikePage = this.formService.form().accessories
      ?.filter(a => !this.extraAccessoriesInBikeSelectionPage.includes(a.bikeType)) || [];

    this.formService.update({
      ...this.formService.form(),
      accessories: [...accNotInBikePage, ...aArray],
      previousSameRental: this.form().previousSameRental,
    });

  }

  private buildDataBikes() {
    const formData = this.formGroup.getRawValue();
    const bikesArray: Bike[] = [];
    Object.keys(formData)
      .filter(key => key.startsWith('bike-') && formData[key].quantity > 0)
      .forEach(key => {
        const bikeType = parseInt(key.split('-')[1]);
        const bikeFormData = formData[key];

        const originalBikeDef = this.bikes().find((b: Bike) => b.bikeType === bikeType)!;
        const previousBikes = this.formService.form()?.previousBikes || [];

        for (let i = 1; i <= bikeFormData.quantity; i++) {
          const id = Number(`${bikeType}${i}`);
          const heightValue = bikeFormData[`height-${i}`] || 0;
          const previousBike = previousBikes
            .find(p => p.bikeType === bikeType && p.height === heightValue && p.id === id);

          const b = {
            ...originalBikeDef,
            id: id,
            quantity: bikeFormData.quantity,
            height: heightValue,
            accessories: previousBike?.accessories || [],
          };
          bikesArray.push(b);
        }
      });

    this.formService.update({
      ...this.form(),
      bikes: bikesArray,
      discountCard: formData.discountCard?.id ? formData.discountCard : undefined,
      previousSameRental: this.form().previousSameRental,
    });
  }

  onMouseWheel = () => null;

  ngOnDestroy() {
    this.testDataSubscription?.unsubscribe();
  }

  protected readonly Number = Number;
}
